import { useState, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Calendar, Package, ChevronUp, ChevronDown, ChevronsUpDown, X, TableProperties, LayoutList, AlertTriangle, CheckCircle2, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { useListNavigationStore } from '@/lib/useListNavigation';
import {
  deriveReservationStatus,
  resolveFulfilledUnits,
  isActiveAOStatus,
  type EffectiveStatus,
} from '@/lib/reservationStatus';
import { HelpTooltip } from '@/components/HelpTooltip';

// ─── Types ───────────────────────────────────────────────────────────────────

type StatusFilter = Set<EffectiveStatus>; // empty set = all
type SortField = 'reservation_code' | 'build_sku' | 'quantity' | 'status' | 'date' | 'fulfillment';
type SortOrder = 'asc' | 'desc';

interface PaoAggregate {
  reservation_id: string;
  pao_count: number;
  active_ao_count: number;
}

type StockReadiness = 'ready' | 'awaiting_stock' | 'unknown';

interface EnrichedReservation {
  id: string;
  reservation_code: string;
  build_sku: string;
  quantity: number;
  quantity_fulfilled: number;
  reservation_date: string | null;
  created_at: string;
  db_status: string;
  effective_status: EffectiveStatus;
  build_description: string | null;
  component_skus: string[];
  component_details: { sku: string; type: string; qty: number }[];
  stock_readiness: StockReadiness;
  actionable_units: number | null; // how many unfulfilled units can be built from on-hand stock
  needs_ordering: boolean;         // true when allocated+expected still can't cover all unfulfilled units
  units_short: number;             // count of units that can't be covered even with on-order stock
  notes: string | null;
}

// ─── Note Indicator ──────────────────────────────────────────────────────────

function NoteIndicator({ note }: { note: string }) {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; above: boolean }>({ top: 0, left: 0, above: true });
  const ref = useRef<HTMLSpanElement>(null);

  const updatePosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const above = rect.top > 120;
    setCoords({
      top: above ? rect.top - 4 : rect.bottom + 4,
      left: Math.max(160, Math.min(rect.left + rect.width / 2, window.innerWidth - 160)),
      above,
    });
  }, []);

  return (
    <span
      ref={ref}
      className="inline-flex items-center ml-1.5 cursor-default"
      onMouseEnter={(e) => { e.stopPropagation(); updatePosition(); setShow(true); }}
      onMouseLeave={() => setShow(false)}
      onClick={(e) => e.stopPropagation()}
    >
      <MessageSquare className="h-3 w-3 text-muted-foreground/60 hover:text-primary transition-colors" />
      {show && createPortal(
        <div
          style={{
            position: 'fixed',
            top: coords.above ? undefined : coords.top,
            bottom: coords.above ? `calc(100vh - ${coords.top}px)` : undefined,
            left: coords.left,
            transform: 'translateX(-50%)',
            zIndex: 99999,
          }}
          className="w-72 px-3 py-2 text-xs leading-relaxed text-popover-foreground bg-popover border border-border rounded-md shadow-lg pointer-events-none whitespace-pre-wrap"
        >
          {note}
        </div>,
        document.body,
      )}
    </span>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<EffectiveStatus, { label: string; color: string; ring: string; badgeClass: string }> = {
  'Pending':     { label: 'Pending',     color: 'text-blue-600',    ring: 'ring-blue-500',    badgeClass: 'bg-blue-100 text-blue-800 border-blue-200' },
  'In Progress': { label: 'In Progress', color: 'text-orange-600',  ring: 'ring-orange-500',  badgeClass: 'bg-orange-100 text-orange-800 border-orange-200' },
  'Partial':     { label: 'Partial',     color: 'text-purple-600',  ring: 'ring-purple-500',  badgeClass: 'bg-purple-100 text-purple-800 border-purple-200' },
  'Completed':   { label: 'Completed',   color: 'text-emerald-600', ring: 'ring-emerald-500', badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  'Cancelled':   { label: 'Cancelled',   color: 'text-red-600',     ring: 'ring-red-500',     badgeClass: 'bg-red-100 text-red-800 border-red-200' },
};

const STATUS_ORDER: EffectiveStatus[] = ['Pending', 'In Progress', 'Partial', 'Completed', 'Cancelled'];

// Component category columns for table view
const COMP_CATEGORIES: { key: string; label: string; prefixes: string[] }[] = [
  { key: 'cpu',    label: 'CPU',        prefixes: ['cpu'] },
  { key: 'fan',    label: 'CPU Cooler', prefixes: ['fan', 'cpu cooler', 'cooler'] },
  { key: 'ram',    label: 'RAM',        prefixes: ['ram', 'memory'] },
  { key: 'mb',     label: 'Motherboard',prefixes: ['mb', 'mobo', 'motherboard'] },
  { key: 'rom',    label: 'Storage',    prefixes: ['rom', 'storage', 'ssd', 'hdd', 'nvme'] },
  { key: 'gpu',    label: 'GPU',        prefixes: ['gpu', 'vga', 'graphics'] },
  { key: 'psu',    label: 'PSU',        prefixes: ['psu', 'power'] },
  { key: 'os',     label: 'OS',         prefixes: ['os', 'windows'] },
  { key: 'case',   label: 'Case',       prefixes: ['cas', 'case', 'chassis'] },
  { key: 'acc',    label: 'ACC',        prefixes: ['acc'] },
];

function matchCategory(type: string): string {
  const lower = type.toLowerCase().replace(/\s*\d+$/, ''); // strip trailing numbers like "ROM 1"
  for (const cat of COMP_CATEGORIES) {
    if (cat.prefixes.some(p => lower === p || lower.startsWith(p))) return cat.key;
  }
  return 'other';
}

// ─── Sort Icon ────────────────────────────────────────────────────────────────

function SortIcon({ field, sortField, sortOrder }: { field: SortField; sortField: SortField; sortOrder: SortOrder }) {
  if (sortField !== field) return <ChevronsUpDown className="h-3.5 w-3.5 ml-1 opacity-40" />;
  return sortOrder === 'asc'
    ? <ChevronUp className="h-3.5 w-3.5 ml-1" />
    : <ChevronDown className="h-3.5 w-3.5 ml-1" />;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ReservationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const componentFilter = searchParams.get('component') || '';
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(new Set());
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [viewMode, setViewMode] = useState<'default' | 'table'>('default');
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [showColMenu, setShowColMenu] = useState(false);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const { data: reservations, isLoading } = useQuery({
    queryKey: ['reservations-enriched'],
    queryFn: async () => {
      // 1. All reservations
      const { data: rawRes, error: resErr } = await supabase
        .from('reservations')
        .select('id, reservation_code, build_sku, quantity, quantity_fulfilled, reservation_date, created_at, status, notes')
        .order('created_at', { ascending: false });
      if (resErr) throw resErr;
      if (!rawRes || rawRes.length === 0) return [];

      // 2. Build descriptions (batch by 100)
      const skus = [...new Set(rawRes.map((r: any) => r.build_sku).filter(Boolean))];
      const buildsMap: Record<string, string> = {};
      for (let i = 0; i < skus.length; i += 100) {
        const { data: bd } = await supabase
          .from('builds').select('sku, description').in('sku', skus.slice(i, i + 100));
        (bd || []).forEach((b: any) => { buildsMap[b.sku] = b.description; });
      }

      // 3. PAO aggregates keyed by reservation_id
      const reservationIds = rawRes.map((r: any) => r.id);
      const paoMap: Record<string, PaoAggregate> = {};
      for (let i = 0; i < reservationIds.length; i += 100) {
        const { data: paoRows } = await supabase
          .from('assembly_orders')
          .select('reservation_id, status, quantity')
          .in('reservation_id', reservationIds.slice(i, i + 100));

        (paoRows || []).forEach((p: any) => {
          const rid = p.reservation_id;
          const aoQty = p.quantity || 1;
          if (!paoMap[rid]) paoMap[rid] = { reservation_id: rid, pao_count: 0, active_ao_count: 0 };
          if (p.status === 'Posted') {
            paoMap[rid].pao_count += aoQty;
          } else if (isActiveAOStatus(p.status)) {
            paoMap[rid].active_ao_count += aoQty;
          }
        });
      }

      // 4. Reservation component SKUs + quantities + types — keyed by reservation_id
      const resCompMap: Record<string, string[]> = {};
      const resCompQtyMap: Record<string, { sku: string; qty: number; allocated: number; expected: number }[]> = {};
      const resCompDetailMap: Record<string, { sku: string; type: string; qty: number }[]> = {};
      const allRcRows: any[] = [];
      for (let i = 0; i < reservationIds.length; i += 50) {
        const { data: rcRows } = await supabase
          .from('reservation_components')
          .select('reservation_id, component_sku, component_type, quantity, allocated_qty, expected_qty')
          .in('reservation_id', reservationIds.slice(i, i + 50));

        (rcRows || []).forEach((rc: any) => {
          allRcRows.push(rc);
          if (!resCompMap[rc.reservation_id]) resCompMap[rc.reservation_id] = [];
          resCompMap[rc.reservation_id].push(rc.component_sku);
          if (!resCompQtyMap[rc.reservation_id]) resCompQtyMap[rc.reservation_id] = [];
          resCompQtyMap[rc.reservation_id].push({ sku: rc.component_sku, qty: rc.quantity || 1, allocated: rc.allocated_qty ?? 0, expected: rc.expected_qty ?? 0 });
          if (!resCompDetailMap[rc.reservation_id]) resCompDetailMap[rc.reservation_id] = [];
          resCompDetailMap[rc.reservation_id].push({ sku: rc.component_sku, type: rc.component_type || '', qty: rc.quantity || 1 });
        });
      }

      // 5. Component-level on_order_qty + expected_qty — to verify PO backing
      const allCompSkus = [...new Set(allRcRows.map((rc: any) => rc.component_sku))];
      const compStockMap: Record<string, { on_order_qty: number; expected_qty: number }> = {};
      for (let i = 0; i < allCompSkus.length; i += 100) {
        const { data: compRows } = await supabase
          .from('components')
          .select('part_number, on_order_qty, expected_qty')
          .in('part_number', allCompSkus.slice(i, i + 100));
        (compRows || []).forEach((c: any) => {
          compStockMap[c.part_number] = { on_order_qty: c.on_order_qty ?? 0, expected_qty: c.expected_qty ?? 0 };
        });
      }


      // 7. Enrich
      return rawRes.map((r: any): EnrichedReservation => {
        const qty = r.quantity ?? 1;
        const fulfilled = resolveFulfilledUnits(paoMap[r.id]?.pao_count || 0, r.quantity_fulfilled);
        const activeAOs = paoMap[r.id]?.active_ao_count || 0;
        const status = deriveReservationStatus(r.status, qty, fulfilled, activeAOs);

        // Stock readiness + actionable units — using per-reservation allocation
        let stock_readiness: StockReadiness = 'unknown';
        let actionable_units: number | null = null;
        let needs_ordering = false;
        let units_short = 0;
        const unfulfilled = Math.max(0, qty - fulfilled);
        const comps = resCompQtyMap[r.id] || [];
        if (comps.length > 0 && unfulfilled > 0 && status !== 'Completed' && status !== 'Cancelled') {
          // Buildable from on-hand stock = min across components of floor(allocated / per_build)
          const maxPerComp = comps.map(c => Math.floor(c.allocated / c.qty));
          actionable_units = Math.min(unfulfilled, Math.min(...maxPerComp));

          // For each component, compute how much of this reservation's expected_qty
          // is actually backed by a real PO (on_order_qty at component level).
          // poAvailable = PO capacity remaining after other reservations' claims.
          const compCoverage = comps.map(c => {
            const cs = compStockMap[c.sku];
            const compOnOrder = cs?.on_order_qty ?? 0;
            const compTotalExpected = cs?.expected_qty ?? c.expected;
            const poAvailable = Math.max(0, compOnOrder - (compTotalExpected - c.expected));
            const poCovered = Math.min(c.expected, poAvailable);
            return { ...c, poCovered, uncovered: c.expected - poCovered };
          });

          const fulfillableWithPOs = Math.min(unfulfilled, Math.min(...compCoverage.map(c => Math.floor((c.allocated + c.poCovered) / c.qty))));
          units_short = Math.max(0, unfulfilled - fulfillableWithPOs);
          needs_ordering = units_short > 0;

          const awaitingStock = compCoverage.some(c => c.poCovered > 0);
          stock_readiness = awaitingStock ? 'awaiting_stock' : (needs_ordering ? 'unknown' : 'ready');
        } else if (status === 'Completed' || status === 'Cancelled') {
          stock_readiness = 'ready';
        }

        return {
          id: r.id,
          reservation_code: r.reservation_code || r.id.slice(0, 8).toUpperCase(),
          build_sku: r.build_sku || '',
          quantity: qty,
          quantity_fulfilled: fulfilled,
          reservation_date: r.reservation_date,
          created_at: r.created_at,
          db_status: r.status,
          effective_status: status,
          build_description: buildsMap[r.build_sku] || null,
          component_skus: resCompMap[r.id] || [],
          component_details: resCompDetailMap[r.id] || [],
          stock_readiness,
          actionable_units,
          needs_ordering,
          units_short,
          notes: r.notes || null,
        };
      });
    },
  });

  // ── Status counts ───────────────────────────────────────────────────────────
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: 0, 'Pending': 0, 'In Progress': 0, 'Partial': 0, 'Completed': 0, 'Cancelled': 0,
    };
    (reservations || []).forEach(r => {
      counts.all++;
      counts[r.effective_status]++;
    });
    return counts;
  }, [reservations]);

  // ── Filter + search + sort ──────────────────────────────────────────────────
  const displayed = useMemo(() => {
    let rows = reservations || [];

    if (statusFilter.size > 0) {
      rows = rows.filter(r => statusFilter.has(r.effective_status));
    }

    if (componentFilter) {
      const cf = componentFilter.toLowerCase();
      rows = rows.filter(r =>
        r.component_skus.some(sku => sku.toLowerCase().includes(cf))
      );
    }

    if (searchQuery) {
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        rows = rows.filter(r =>
          r.reservation_code.toLowerCase().includes(q) ||
          r.build_sku.toLowerCase().includes(q) ||
          (r.build_description?.toLowerCase().includes(q) ?? false) ||
          r.component_skus.some(sku => sku.toLowerCase().includes(q))
        );
      }
    }

    // Column filters (table view)
    const activeColFilters = Object.entries(colFilters).filter(([, v]) => v.trim() !== '');
    if (activeColFilters.length > 0) {
      rows = rows.filter(r => activeColFilters.every(([col, val]) => {
        const v = val.trim().toLowerCase();
        switch (col) {
          case 'reservation_code': return r.reservation_code.toLowerCase().includes(v);
          case 'build_sku': return r.build_sku.toLowerCase().includes(v) || (r.build_description?.toLowerCase().includes(v) ?? false);
          case 'quantity': return String(r.quantity).includes(v);
          case 'status': return r.effective_status.toLowerCase().includes(v);
          case 'date': return new Date(r.reservation_date || r.created_at).toLocaleDateString('en-GB').includes(v);
          case 'fulfillment': return `${r.quantity_fulfilled}/${r.quantity}`.includes(v);
          case 'stock': {
            if (v === 'ready' || v === 'yes') return r.stock_readiness === 'ready';
            if (v === 'awaiting' || v === 'no' || v === 'waiting') return r.stock_readiness === 'awaiting_stock';
            return r.stock_readiness.toLowerCase().includes(v);
          }
          default: {
            // Component category columns: match SKU
            const catDef = COMP_CATEGORIES.find(c => c.key === col);
            if (catDef) {
              const comps = r.component_details.filter(c => matchCategory(c.type) === col);
              return comps.some(c => c.sku.toLowerCase().includes(v));
            }
            return true;
          }
        }
      }));
    }

    rows = [...rows].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'reservation_code': cmp = a.reservation_code.localeCompare(b.reservation_code); break;
        case 'build_sku':        cmp = a.build_sku.localeCompare(b.build_sku); break;
        case 'quantity':         cmp = a.quantity - b.quantity; break;
        case 'status':           cmp = STATUS_ORDER.indexOf(a.effective_status) - STATUS_ORDER.indexOf(b.effective_status); break;
        case 'fulfillment':      cmp = (a.quantity_fulfilled / a.quantity) - (b.quantity_fulfilled / b.quantity); break;
        case 'date':
        default:
          cmp = new Date(a.reservation_date || a.created_at).getTime() -
                new Date(b.reservation_date || b.created_at).getTime();
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return rows;
  }, [reservations, statusFilter, searchQuery, componentFilter, sortField, sortOrder, colFilters]);

  // Store visible IDs for prev/next navigation on detail page
  useListNavigationStore('reservations', displayed.map(r => r.id));

  // Component categories that have at least one entry across all reservations
  const visibleCategories = useMemo(() => {
    if (!reservations) return [];
    const usedKeys = new Set<string>();
    reservations.forEach(r => {
      r.component_details.forEach(c => usedKeys.add(matchCategory(c.type)));
    });
    return COMP_CATEGORIES.filter(cat => usedKeys.has(cat.key) && !hiddenCols.has(cat.key));
  }, [reservations, hiddenCols]);

  const allCategories = useMemo(() => {
    if (!reservations) return [];
    const usedKeys = new Set<string>();
    reservations.forEach(r => {
      r.component_details.forEach(c => usedKeys.add(matchCategory(c.type)));
    });
    return COMP_CATEGORIES.filter(cat => usedKeys.has(cat.key));
  }, [reservations]);

  // ── Sortable TH ─────────────────────────────────────────────────────────────
  const Th = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <th
      className="text-left p-4 font-semibold cursor-pointer select-none hover:bg-muted/70 transition-colors"
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center">
        {children}
        <SortIcon field={field} sortField={sortField} sortOrder={sortOrder} />
      </span>
    </th>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className={viewMode === 'table' ? 'flex flex-col h-[calc(100vh-4rem)]' : 'flex flex-col h-[calc(100vh-4rem)]'}>
      <div className="space-y-6 shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Reservations</h1>
          <p className="text-muted-foreground">Manage component reservations and build orders</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            title={viewMode === 'default' ? 'Table view' : 'Default view'}
            onClick={() => { setViewMode(v => v === 'default' ? 'table' : 'default'); setColFilters({}); }}
          >
            {viewMode === 'default' ? <TableProperties className="h-4 w-4" /> : <LayoutList className="h-4 w-4" />}
          </Button>
          {viewMode === 'table' && (
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => setShowColMenu(v => !v)}>
                Columns {hiddenCols.size > 0 && <Badge variant="secondary" className="ml-1 text-xs">{hiddenCols.size} hidden</Badge>}
              </Button>
              {showColMenu && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-popover border rounded-md shadow-md p-2 min-w-[180px]">
                  {allCategories.map(cat => (
                    <label key={cat.key} className="flex items-center gap-2 px-2 py-1 text-sm cursor-pointer hover:bg-muted/50 rounded">
                      <input
                        type="checkbox"
                        checked={!hiddenCols.has(cat.key)}
                        onChange={() => setHiddenCols(prev => {
                          const next = new Set(prev);
                          if (next.has(cat.key)) next.delete(cat.key); else next.add(cat.key);
                          return next;
                        })}
                        className="h-3.5 w-3.5 rounded border-input accent-primary cursor-pointer"
                      />
                      {cat.label}
                    </label>
                  ))}
                  {hiddenCols.size > 0 && (
                    <button className="text-xs text-muted-foreground hover:text-foreground underline px-2 pt-1" onClick={() => setHiddenCols(new Set())}>
                      Show all
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <Button onClick={() => navigate('/reservations/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Reservation
          </Button>
        </div>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        <Card
          className={`cursor-pointer transition-all ${statusFilter.size === 0 ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
          onClick={() => setStatusFilter(new Set())}
        >
          <CardContent className="pt-5 pb-4">
            <div className="text-2xl font-bold">{statusCounts.all}</div>
            <p className="text-xs text-muted-foreground mt-0.5">Total</p>
          </CardContent>
        </Card>

        {(['Pending', 'In Progress', 'Partial', 'Completed', 'Cancelled'] as EffectiveStatus[]).map(s => {
          const cfg = STATUS_CONFIG[s];
          const active = statusFilter.has(s);
          return (
            <Card
              key={s}
              className={`cursor-pointer transition-all ${active ? `ring-2 ${cfg.ring}` : 'hover:shadow-md'}`}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  // Toggle this status in multi-select
                  setStatusFilter(prev => {
                    const next = new Set(prev);
                    if (next.has(s)) next.delete(s);
                    else next.add(s);
                    return next;
                  });
                } else {
                  // Single select (or deselect if already sole selection)
                  setStatusFilter(prev => prev.size === 1 && prev.has(s) ? new Set() : new Set([s]));
                }
              }}
            >
              <CardContent className="pt-5 pb-4">
                <div className={`text-2xl font-bold ${cfg.color}`}>{statusCounts[s]}</div>
                <p className="text-xs text-muted-foreground mt-0.5">{cfg.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Component filter indicator */}
      {componentFilter && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm px-3 py-1.5 bg-blue-50 border-blue-200 text-blue-700">
            Filtered by component: <span className="font-mono font-semibold ml-1">{componentFilter}</span>
            <button
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('component');
                setSearchParams(next, { replace: true });
              }}
              className="ml-2 hover:text-blue-900"
            >
              <X className="h-3.5 w-3.5 inline" />
            </button>
          </Badge>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by reservation code, build SKU, description, or component..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 pr-9"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      </div>
      {/* Table */}
      <Card className="mt-6 flex-1 min-h-0 flex flex-col">
        <CardContent className="p-0 flex-1 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : displayed.length > 0 ? (
            <div className="overflow-auto h-full">
              <table className={viewMode === 'table' ? 'text-sm min-w-max' : 'w-full text-sm'}>
                <thead className="border-b bg-background sticky top-0 z-10">
                  <tr>
                    <Th field="reservation_code">Reservation</Th>
                    <Th field="build_sku">Build</Th>
                    <Th field="quantity">Qty</Th>
                    <Th field="status">Status <HelpTooltip tipKey="reservation.status" /></Th>
                    <Th field="date">Date</Th>
                    <Th field="fulfillment">Fulfillment <HelpTooltip tipKey="reservation.fulfillment" /></Th>
                    {viewMode === 'table' && <th className="text-left p-4 font-semibold">Stock <HelpTooltip tipKey="reservation.actionableUnits" /></th>}
                    {viewMode === 'table' && visibleCategories.map(cat => (
                      <th key={cat.key} className="text-left p-4 font-semibold text-xs whitespace-nowrap">{cat.label}</th>
                    ))}
                  </tr>
                  {viewMode === 'table' && (
                    <tr className="border-b bg-muted/30">
                      {['reservation_code', 'build_sku', 'quantity', 'status', 'date', 'fulfillment', 'stock'].map(col => (
                        <th key={col} className="px-4 py-1.5">
                          <input
                            type="text"
                            placeholder="Filter…"
                            className="w-full h-7 px-2 text-xs font-normal rounded border border-input bg-background"
                            value={colFilters[col] || ''}
                            onChange={e => setColFilters(prev => ({ ...prev, [col]: e.target.value }))}
                          />
                        </th>
                      ))}
                      {visibleCategories.map(cat => (
                        <th key={cat.key} className="px-4 py-1.5">
                          <input
                            type="text"
                            placeholder="Filter…"
                            className="w-full h-7 px-2 text-xs font-normal rounded border border-input bg-background"
                            value={colFilters[cat.key] || ''}
                            onChange={e => setColFilters(prev => ({ ...prev, [cat.key]: e.target.value }))}
                          />
                        </th>
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody className="divide-y">
                  {displayed.map((res) => {
                    const cfg = STATUS_CONFIG[res.effective_status];
                    const pct = res.quantity > 0 ? Math.round((res.quantity_fulfilled / res.quantity) * 100) : 0;

                    return (
                      <tr
                        key={res.id}
                        className="hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/reservations/${res.id}`)}
                      >
                        <td className="p-4">
                          <div className="font-mono font-semibold text-primary text-sm inline-flex items-center">
                            {res.reservation_code}
                            {res.notes && <NoteIndicator note={res.notes} />}
                          </div>
                          {!viewMode.startsWith('table') && res.reservation_date && (
                            <div className="text-xs text-muted-foreground">
                              {new Date(res.reservation_date).toLocaleDateString('en-GB')}
                            </div>
                          )}
                        </td>

                        <td className="p-4">
                          <div className="font-medium font-mono text-xs">{res.build_sku}</div>
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {res.build_description}
                          </div>
                        </td>

                        <td className="p-4">
                          <Badge variant="outline" className="whitespace-nowrap">
                            {res.quantity} {res.quantity === 1 ? 'unit' : 'units'}
                          </Badge>
                        </td>

                        <td className="p-4">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge className={`text-xs border whitespace-nowrap ${cfg.badgeClass}`}>
                              {cfg.label}
                            </Badge>
                            {res.needs_ordering && res.effective_status !== 'Completed' && res.effective_status !== 'Cancelled' && (
                              <span
                                className="inline-flex items-center gap-1 text-xs text-red-600 font-medium whitespace-nowrap"
                                title={`${res.units_short} unit${res.units_short !== 1 ? 's' : ''} cannot be fulfilled — components need to be ordered`}
                              >
                                <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                                {res.units_short > 1 ? `${res.units_short} units` : '1 unit'} need ordering
                              </span>
                            )}
                            {!res.needs_ordering && res.stock_readiness === 'awaiting_stock' && res.effective_status !== 'Completed' && res.effective_status !== 'Cancelled' && (
                              <span title="Some components are on order — awaiting delivery">
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="p-4">
                          <div className="flex items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
                            <Calendar className="h-3 w-3 flex-shrink-0" />
                            {new Date(res.reservation_date || res.created_at).toLocaleDateString('en-GB')}
                          </div>
                        </td>

                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 max-w-[80px]">
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-green-500 transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {res.quantity_fulfilled}/{res.quantity}
                            </span>
                          </div>
                        </td>

                        {viewMode === 'table' && (() => {
                          const unf = Math.max(0, res.quantity - res.quantity_fulfilled);
                          const act = res.actionable_units;
                          const isDone = res.effective_status === 'Completed' || res.effective_status === 'Cancelled';
                          return (
                            <td className="p-4 whitespace-nowrap">
                              {isDone ? (
                                <span className="text-xs text-muted-foreground">—</span>
                              ) : act !== null && act >= unf ? (
                                <span className="inline-flex items-center gap-1 text-xs text-green-700">
                                  <CheckCircle2 className="h-3.5 w-3.5" /> {unf}/{unf}
                                </span>
                              ) : act !== null && act > 0 ? (
                                <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                                  <AlertTriangle className="h-3.5 w-3.5" /> {act}/{unf}
                                </span>
                              ) : act === 0 ? (
                                <span className="inline-flex items-center gap-1 text-xs text-red-500">
                                  <AlertTriangle className="h-3.5 w-3.5" /> 0/{unf}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                          );
                        })()}
                        {viewMode === 'table' && visibleCategories.map(cat => {
                          const comps = res.component_details.filter(c => matchCategory(c.type) === cat.key);
                          return (
                            <td key={cat.key} className="p-4">
                              {comps.length > 0 ? (
                                <div className="space-y-0.5">
                                  {comps.map((c, i) => (
                                    <div key={i} className="font-mono text-xs whitespace-nowrap" title={c.sku}>
                                      {c.sku}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {viewMode === 'table' && Object.values(colFilters).some(v => v.trim()) && (
                <div className="px-4 py-2 border-t text-xs text-muted-foreground">
                  {displayed.length} result{displayed.length !== 1 ? 's' : ''}
                  <button className="ml-2 underline hover:text-foreground" onClick={() => setColFilters({})}>
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Package className="h-12 w-12 text-muted-foreground opacity-50 mb-4" />
              <h3 className="font-semibold text-lg mb-2">No reservations found</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {searchQuery || statusFilter.size > 0
                  ? 'Try adjusting your search or filters'
                  : 'Get started by creating a new reservation'}
              </p>
              {!searchQuery && statusFilter.size === 0 && (
                <Button onClick={() => navigate('/reservations/new')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Reservation
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
